<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { useAnnotationTokenHighlight } from "~/stores/useAnnotationTokenHighlight";

const annotationTokenHighlightStore = useAnnotationTokenHighlight();
const { activeAnnotation } = storeToRefs(annotationTokenHighlightStore);

const selectedTab = ref<"checklist" | "compare">("checklist");
const setSelectedTab = (tab: "checklist" | "compare") => {
  selectedTab.value = tab;
};

const documentId = inject("documentId") as any;

const documentStore = useSubmittedDocumentStore(documentId);
const { submittedDocument } = storeToRefs(documentStore);

const documentToCompareStore = useDocumentToCompareStore(documentId);
const { documentIdToCompare, hasDocumentToCompare, canCompareDocuments } =
  storeToRefs(documentToCompareStore);

const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isTeacherViewMode } = storeToRefs(documentViewModeStore);

const userClassroomStore = useUserClassroomsDataStore();
const { allAssignments, allClassrooms } = storeToRefs(userClassroomStore);

const showCompareTabs = computed(() => {
  if (isTeacherViewMode.value != true) {
    return false;
  }

  return canCompareDocuments.value == true;
});

const allowChecklists = computed(() => {
  const allowChecklistForSubmission = useAllowChecklistForSubmission(
    submittedDocument.value
  );

  if (allowChecklistForSubmission != undefined) {
    return allowChecklistForSubmission;
  }

  const matchingAssignment = allAssignments.value.find(
    (assignment) => assignment.id == submittedDocument.value?.assignmentId
  );

  if (matchingAssignment == undefined) return true;

  if (matchingAssignment.requireRoughDraftChecklist != undefined) {
    return matchingAssignment.requireRoughDraftChecklist;
  }

  return true;
});

const checklistTabLabel = computed(() => {
  if (isTeacherViewMode.value != true) {
    return "Review";
  }

  if (allowChecklists.value != undefined) {
    return allowChecklists.value ? "Checklist" : "Comments";
  }

  if (submittedDocument.value?.assignmentType != undefined) {
    return submittedDocument.value.assignmentType == AssignmentType.general
      ? "Comments"
      : "Checklist";
  }

  return submittedDocument.value?.documentSubmissionType ==
    DocumentSubmissionType.keyWordOutline
    ? "Comments"
    : "Checklist";
});

const checklistTabIcon = computed(() => {
  if (isTeacherViewMode.value != true) {
    return "lucide:notebook-text";
  }

  const allowChecklistForSubmission = useAllowChecklistForSubmission(
    submittedDocument.value
  );

  if (allowChecklistForSubmission != undefined) {
    return allowChecklistForSubmission
      ? "material-symbols:checklist"
      : "lucide:notebook-text";
  }

  if (submittedDocument.value?.assignmentType != undefined) {
    return submittedDocument.value.assignmentType == AssignmentType.general
      ? "lucide:notebook-text"
      : "material-symbols:checklist";
  }

  return submittedDocument.value?.documentSubmissionType ==
    DocumentSubmissionType.keyWordOutline
    ? "lucide:notebook-text"
    : "material-symbols:checklist";
});

const tabs = computed(() => {
  const t = [
    {
      key: "checklist",
      label: checklistTabLabel.value,
      icon: checklistTabIcon.value,
    },
  ];

  if (showCompareTabs.value) {
    t.push({
      key: "compare",
      label: "Compare",
      icon: "carbon:compare",
    });
  }

  return t;
});
</script>

<template>
  <div
    data-step-id="document-panel-container"
    id="document-panel-container"
    class="bg-surface shrink-0 transition-all"
    :class="{
      ' opacity-20': activeAnnotation && activeAnnotation.commentObject,
      'desktop:w-[550px] w-[400px]':
        selectedTab == 'checklist' || showCompareTabs != true,
      'desktop:w-[800px] w-[800px]':
        selectedTab != 'checklist' && showCompareTabs == true,
    }"
  >
    <div class="h-full flex flex-col">
      <div class="p-1 border-b">
        <BaseTabBar class="shrink-0" :tabs="tabs" v-model="selectedTab" />
      </div>
      <!-- <div class="flex flex-row items-center w-full border-b p-2">
        <div
          class="grow flex flex-row justify-center items-center p-2 text-primary-default cursor-pointer rounded"
          @click="setSelectedTab('checklist')"
          :class="{
            'bg-primary-default bg-opacity-20 hover:bg-opacity-30':
              selectedTab == 'checklist' && showCompareTabs,
            'hover:bg-light-default': selectedTab != 'checklist',
          }"
        >
          <Icon
            name="material-symbols:checklist"
            size="20"
            class="mt-[2px] mr-2"
          />
          <span class="text-h5">
            {{ checklistTabLabel }}
          </span>
        </div>
        <div
          v-if="showCompareTabs"
          class="grow flex flex-row justify-center items-center p-2 text-primary-default cursor-pointer rounded"
          @click="setSelectedTab('compare')"
          :class="{
            'bg-primary-default bg-opacity-20 hover:bg-opacity-30':
              selectedTab == 'compare',
            'hover:bg-light-default': selectedTab != 'compare',
          }"
        >
          <Icon name="carbon:compare" size="20" class="mt-[2px] mr-2" />
          <span class="text-h5">Compare</span>
        </div>
      </div> -->
      <div class="grow min-h-0">
        <ChecklistContainer v-if="selectedTab == 'checklist'" />
        <KeepAlive>
          <DocumentPanelCompareContainer v-if="selectedTab == 'compare'" />
        </KeepAlive>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
